import type { Game, Registration } from '@swtorbit/shared-socket-events';
import {
  SocketEvent,
  GameType,
  canTransition,
  validateMap,
  trlImageAreaKey,
  resolveTrlTriangulationConfig,
  TRL_SIMULTANEOUS_MIN_TEAM_SIZE,
  getHnstTeamsNotReady,
  type GameStatus,
} from '@swtorbit/shared-socket-events';
import { getEnabledPlayers } from '@swtorbit/shared-react-hooks';
import { hnstUnevenHidingWarning } from './hnstUnevenHiding';

export interface TransitionConditionResult {
  met: boolean;
  missingItems: string[];
}

export interface TransitionExecuteContext {
  game: Game;
  socket: { emit: (event: string, ...args: unknown[]) => void };
  startGame: (req: { roomCode: string }) => void;
}

export interface LifecycleTransition {
  id: string;
  /** Target status this transition advances the game to. */
  targetStatus: GameStatus;
  label: string;
  description: string;
  buttonVariant: 'primary' | 'secondary' | 'danger';
  checkConditions?: (game: Game, registrations: Registration[]) => TransitionConditionResult;
  /**
   * Heading shown above the unmet-condition list in the lifecycle modal. Defaults
   * to the registration-completion wording when omitted (back-compat with FPR).
   */
  conditionsLabel?: string;
  /** Things the admin should know before going ahead; unlike conditions, they don't block it. */
  checkWarnings?: (game: Game) => string[];
  execute: (ctx: TransitionExecuteContext) => void;
}

function emitStatusTransition(status: GameStatus) {
  return ({ game, socket }: TransitionExecuteContext) => {
    socket.emit(SocketEvent.ADMIN_UPDATE_GAME_STATUS, {
      roomCode: game.roomCode,
      status,
    });
  };
}

const openRegistrationTransition: LifecycleTransition = {
  id: 'open-registration',
  targetStatus: 'pre_registration',
  label: 'Open Registration',
  description:
    'Open registration so players can sign up via the Discord /registration command. You can configure a required Discord role in the Registration Settings on the Game tab.',
  buttonVariant: 'primary',
  execute: ({ game, socket }) => {
    socket.emit(SocketEvent.ADMIN_UPDATE_GAME_STATUS, {
      roomCode: game.roomCode,
      status: 'pre_registration' as GameStatus,
      preRegistrationRoleId: game.preRegistrationRoleId ?? null,
    });
  },
};

const closeRegistrationTransition: LifecycleTransition = {
  id: 'close-registration',
  targetStatus: 'draft',
  label: 'Close Registration',
  description:
    'Close registration and return the game to draft. Existing registrations are preserved and will be available if you re-open registration.',
  buttonVariant: 'secondary',
  execute: emitStatusTransition('draft'),
};

function checkFprPoolCreationConditions(game: Game): TransitionConditionResult {
  const unregistered = getEnabledPlayers(game).filter(
    (p) => !p.fprRegistration || p.fprRegistration.roles.length === 0,
  );
  if (unregistered.length === 0) return { met: true, missingItems: [] };
  return {
    met: false,
    missingItems: unregistered.map((p) => {
      const name = p.characterName ?? 'Unknown';
      const discord = p.discord?.discordUsername ?? p.playerId.slice(-4);
      return `${name} (${discord}) — no role selected`;
    }),
  };
}

const beginPoolCreationTransition: LifecycleTransition = {
  id: 'begin-pool-creation',
  targetStatus: 'pool_creation',
  label: 'Begin Pool Creation',
  description:
    'Move to the pool creation phase. The system will assign team leaders and sort all players into role pools (tank/healer/dps). You can review and adjust assignments before confirming. All active players must have completed their role registration before proceeding.',
  buttonVariant: 'primary',
  checkConditions: (game) => checkFprPoolCreationConditions(game),
  execute: emitStatusTransition('pool_creation'),
};

const reopenRegistrationTransition: LifecycleTransition = {
  id: 'reopen-registration',
  targetStatus: 'pre_registration',
  label: 'Re-open Registration',
  description:
    'Return to pre-registration to allow more players to register or update their submissions. Pool assignments will be cleared.',
  buttonVariant: 'secondary',
  execute: emitStatusTransition('pre_registration'),
};

const fprBeginPreGameTransition: LifecycleTransition = {
  id: 'begin-pre-game',
  targetStatus: 'pre_game',
  label: 'Begin Pre-Game',
  description:
    'Move teams into their Discord channels so they can discuss and agree on a team name. Team leaders can rename their team from the Electron client. Discord channels will be created and players moved if configured.',
  buttonVariant: 'primary',
  execute: emitStatusTransition('pre_game'),
};

const fprGoToLobbyTransition: LifecycleTransition = {
  id: 'go-to-lobby',
  targetStatus: 'lobby',
  label: 'Go to Lobby',
  description:
    'Lock all team names and move the game into the lobby, ready for round 1. Team leaders will no longer be able to rename their team. Starting a round will move the game from the lobby into the active round.',
  buttonVariant: 'primary',
  execute: emitStatusTransition('lobby'),
};

const beginDraftTransition: LifecycleTransition = {
  id: 'begin-draft',
  targetStatus: 'team_creation',
  label: 'Begin Draft',
  description:
    'Advance to the draft phase where team leaders pick players from the confirmed pools. Pools must be confirmed before proceeding.',
  buttonVariant: 'primary',
  checkConditions: (game) => {
    if (game.fprPoolState?.status === 'confirmed') {
      return { met: true, missingItems: [] };
    }
    return { met: false, missingItems: ['Pools must be confirmed before starting the draft'] };
  },
  execute: emitStatusTransition('team_creation'),
};

const hnsGoToLobbyTransition: LifecycleTransition = {
  id: 'go-to-lobby',
  targetStatus: 'lobby',
  label: 'Go to Lobby',
  description: 'Move the game into the lobby. Starting a round will move the game from the lobby into the active round.',
  buttonVariant: 'primary',
  execute: emitStatusTransition('lobby'),
};

// Trailblazers "Go to Lobby" (draft → lobby). Currently identical to the HNS
// transition, but each game owns its own lifecycle transitions so they can
// diverge freely as Trailblazers is built out. Do not merge these.
const hnstBeginTeamCreationTransition: LifecycleTransition = {
  id: 'begin-team-creation',
  targetStatus: 'team_creation',
  label: 'Make Teams',
  description:
    'Move to team creation. Choose a Team Captain for the Rotworms and the Frogdogs, then fill the teams by hand or let the captains take turns picking in a snake draft.',
  buttonVariant: 'primary',
  execute: emitStatusTransition('team_creation'),
};

const hnstGoToLobbyTransition: LifecycleTransition = {
  id: 'go-to-lobby',
  targetStatus: 'lobby',
  label: 'Go to Lobby',
  description:
    'Lock the teams and move the game into the lobby. Team voice channels are created if configured. Decide who hides first (set it or flip the coin), then start round 1.',
  buttonVariant: 'primary',
  conditionsLabel: 'Both teams need a Team Captain, the draft must be finished, and every player must be on a team:',
  checkConditions: (game) => {
    const missingItems = getHnstTeamsNotReady(game);
    return { met: missingItems.length === 0, missingItems };
  },
  execute: emitStatusTransition('lobby'),
};

const trlGoToLobbyTransition: LifecycleTransition = {
  id: 'go-to-lobby',
  targetStatus: 'lobby',
  label: 'Go to Lobby',
  description:
    'Validate and lock the galaxy map, then move the game into the lobby where teams pick their starting planet. The map can no longer be edited once the lobby opens. Starting a round will move the game from the lobby into the active round.',
  buttonVariant: 'primary',
  // Client-side pre-flight so the admin sees exactly why the map can't start.
  // The server re-validates authoritatively in ADMIN_UPDATE_GAME_STATUS.
  checkConditions: (game) => checkTrlMapStartConditions(game),
  execute: emitStatusTransition('lobby'),
};

/**
 * Mirror of the server's `validateTrlMapForStart`: the galaxy map must exist,
 * have at least one planet, and pass the shared topology/image validation before
 * the game can enter the lobby. Runs over the coord-stripped snapshot map, using
 * the coord-free `hasTriangulationTarget` flag (the answer coords never reach us).
 */
function checkTrlMapStartConditions(game: Game): TransitionConditionResult {
  const map = game.trlMap;
  if (!map || map.nodes.length === 0) {
    return { met: false, missingItems: ['Design and save the galaxy map before starting the game'] };
  }
  const validation = validateMap(
    map.nodes.map((n) => ({
      id: n.id,
      imageCount: n.images.length,
      imagesToClear: n.imagesToClear,
      roundType: n.roundType,
      imageAreaKeys: n.images.map(trlImageAreaKey).filter((key): key is string => !!key),
      imageFactions: n.images.map((i) => i.faction).filter((f): f is string => !!f),
      hasTriangulationTarget: n.hasTriangulationTarget ?? false,
    })),
    map.edges.map((e) => ({ fromNodeId: e.fromNodeId, toNodeId: e.toNodeId })),
  );
  if (validation.ok) return { met: true, missingItems: [] };
  return { met: false, missingItems: validation.errors.map((err) => err.message) };
}

// Trailblazers "Start Game" (lobby → active). This is the lobby→active lifecycle
// step: it locks each team's chosen starting planet and begins fog-of-war play.
// Every team must have a captain AND a captain-selected starting planet first.
const trlStartGameTransition: LifecycleTransition = {
  id: 'start-game',
  targetStatus: 'active',
  label: 'Start Game',
  description:
    'Lock in every team\'s starting planet and begin the game. Teams travel the galaxy map from their chosen start; the map cannot be re-picked once the game is active.',
  buttonVariant: 'primary',
  conditionsLabel: 'Every team needs a captain and a chosen starting planet before the game can start:',
  checkConditions: (game) => checkTrlStartGameConditions(game),
  // Start Game is driven by GAME_START (not a plain status update) so the server
  // runs its full preflight/commit (lock starts, seed progress, reveal map).
  execute: ({ game, startGame }) => startGame({ roomCode: game.roomCode }),
};

/**
 * Gate for lobby → active: every team must have a captain, that captain must
 * have selected a starting planet, and — under simultaneous triangulation
 * broadcast — every team must be big enough to work all 3 points at once.
 * Mirrors the server's `preflightTrlStart`.
 */
function checkTrlStartGameConditions(game: Game): TransitionConditionResult {
  const teams = game.trlTeams ?? [];
  if (teams.length === 0) {
    return { met: false, missingItems: ['Create at least one team before starting the game'] };
  }
  const startByTeam = new Map(
    (game.trlTeamProgress ?? []).map((p) => [p.teamId, p.startNodeId]),
  );
  const needsThree = resolveTrlTriangulationConfig(game.trlTriangulationConfig).simultaneousBroadcast;
  const missing: string[] = [];
  for (const team of teams) {
    if (!team.captainPlayerId) {
      missing.push(`${team.name} — no captain assigned`);
    } else if (!startByTeam.get(team.id)) {
      missing.push(`${team.name} — captain has not picked a starting planet`);
    } else if (needsThree && team.members.length < TRL_SIMULTANEOUS_MIN_TEAM_SIZE) {
      missing.push(
        `${team.name} — needs ${TRL_SIMULTANEOUS_MIN_TEAM_SIZE} players for simultaneous ` +
          `broadcast (has ${team.members.length})`,
      );
    }
  }
  if (missing.length === 0) return { met: true, missingItems: [] };
  return { met: false, missingItems: missing };
}

const returnToLobbyTransition: LifecycleTransition = {
  id: 'return-to-lobby',
  targetStatus: 'lobby',
  label: 'Skip to Lobby',
  description:
    'Skip the remaining end-of-round animation and return the game to the lobby immediately. Cumulative scores will be shown so you can prepare for the next round.',
  buttonVariant: 'secondary',
  execute: emitStatusTransition('lobby'),
};

const endGameTransition: LifecycleTransition = {
  id: 'end-game',
  targetStatus: 'completed',
  label: 'End Game',
  description:
    'Mark the game as completed. All rounds will be finalised and results locked. Players will see their final scores.',
  buttonVariant: 'primary',
  execute: emitStatusTransition('completed'),
};

/** Hide and Seek Teams' End Game warns when one team has hidden more rounds. */
const hnstEndGameTransition: LifecycleTransition = {
  ...endGameTransition,
  checkWarnings: (game) => {
    const warning = hnstUnevenHidingWarning(game);
    return warning ? [warning] : [];
  },
};

const abandonTransition: LifecycleTransition = {
  id: 'abandon',
  targetStatus: 'abandoned',
  label: 'Abandon Game',
  description:
    'Permanently abandon this game. All progress will be discarded and connected players will be notified. This action cannot be undone.',
  buttonVariant: 'danger',
  execute: emitStatusTransition('abandoned'),
};

/**
 * Per-game registry of admin-exposed lifecycle transitions keyed by source status.
 * Order matters — it defines button order in the lifecycle modal. Not every legal
 * lifecycle transition is admin-exposed (e.g. `active → round_ending` is automated;
 * `lobby → active` is driven by Start Round, not the lifecycle modal).
 */
const TRANSITIONS_BY_GAME: Record<GameType, Partial<Record<GameStatus, LifecycleTransition[]>>> = {
  [GameType.FP_RACE]: {
    draft: [openRegistrationTransition, beginPoolCreationTransition, abandonTransition],
    pre_registration: [closeRegistrationTransition, beginPoolCreationTransition, abandonTransition],
    pool_creation: [beginDraftTransition, reopenRegistrationTransition, abandonTransition],
    team_creation: [fprBeginPreGameTransition, abandonTransition],
    pre_game: [fprGoToLobbyTransition, abandonTransition],
    lobby: [endGameTransition, abandonTransition],
    active: [endGameTransition, abandonTransition],
    round_ending: [returnToLobbyTransition, endGameTransition, abandonTransition],
  },
  [GameType.HIDE_AND_SEEK_TEAMS]: {
    draft: [openRegistrationTransition, hnstBeginTeamCreationTransition, abandonTransition],
    pre_registration: [closeRegistrationTransition, hnstBeginTeamCreationTransition, abandonTransition],
    team_creation: [hnstGoToLobbyTransition, abandonTransition],
    lobby: [hnstEndGameTransition, abandonTransition],
    active: [hnstEndGameTransition, abandonTransition],
    round_ending: [returnToLobbyTransition, hnstEndGameTransition, abandonTransition],
  },
  [GameType.HIDE_AND_SEEK]: {
    draft: [openRegistrationTransition, hnsGoToLobbyTransition, abandonTransition],
    pre_registration: [closeRegistrationTransition, hnsGoToLobbyTransition, abandonTransition],
    lobby: [endGameTransition, abandonTransition],
    active: [endGameTransition, abandonTransition],
    round_ending: [returnToLobbyTransition, endGameTransition, abandonTransition],
  },
  [GameType.TRAILBLAZERS]: {
    // Trailblazers has an optional Discord-only pre_registration phase for collecting
    // sign-ups. It's informational (never gates a transition); teams are still created
    // by the admin in the lobby. Both draft and pre_registration can advance straight
    // to the lobby via Go to Lobby.
    draft: [openRegistrationTransition, trlGoToLobbyTransition, abandonTransition],
    pre_registration: [closeRegistrationTransition, trlGoToLobbyTransition, abandonTransition],
    lobby: [trlStartGameTransition, endGameTransition, abandonTransition],
    active: [endGameTransition, abandonTransition],
    round_ending: [returnToLobbyTransition, endGameTransition, abandonTransition],
  },
};

export function getAvailableTransitions(game: Game): LifecycleTransition[] {
  const registered = TRANSITIONS_BY_GAME[game.gameType]?.[game.status] ?? [];
  // Filter through the lifecycle module so it remains the single source of truth
  // for which transitions are legal. Any registry drift is silently corrected.
  return registered.filter((t) => canTransition(game.gameType, game.status, t.targetStatus));
}
